import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const STATUS_LABELS = {
  mailbox_active: "Active",
  mailbox_ready_pending_dns: "Active — Awaiting DNS",
  pending_setup: "Setting Up",
  awaiting_customer_dns: "Awaiting DNS",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

const STATUS_COLORS = {
  mailbox_active: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10",
  mailbox_ready_pending_dns: "text-amber-300 border-amber-400/30 bg-amber-500/10",
  pending_setup: "text-sky-300 border-sky-400/30 bg-sky-500/10",
  awaiting_customer_dns: "text-amber-300 border-amber-400/30 bg-amber-500/10",
  cancelled: "text-rose-300 border-rose-400/30 bg-rose-500/10",
  refunded: "text-slate-400 border-slate-600 bg-slate-800/40",
};

const MAILBOX_STATUS_LABELS = {
  active: "Mailbox Active",
  pending: "Provisioning Pending",
  failed: "Provisioning Failed — Manual Setup Required",
};

const MAILBOX_STATUS_COLORS = {
  active: "text-emerald-300",
  pending: "text-amber-300",
  failed: "text-rose-300",
};

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy"
      className="ml-2 rounded-lg border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-semibold text-slate-300 transition hover:bg-white/15"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const Row = ({ label, value, mono = false }) => {
  if (!value) return null;

  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/5 py-3 last:border-b-0 last:pb-0">
      <span className="min-w-[120px] text-sm text-slate-400">{label}</span>
      <span
        className={`text-right text-sm font-medium text-white ${mono ? "font-mono" : ""}`}
      >
        {value}
        {mono ? <CopyButton value={value} /> : null}
      </span>
    </div>
  );
};

const MailboxCard = ({ mailbox }) => {
  const [showDns, setShowDns] = useState(false);
  const credentials = mailbox.mailboxCredentials || {};
  const provisioning = mailbox.provisioningDetails || {};
  const orderStatusClass =
    STATUS_COLORS[mailbox.orderStatus] ||
    "text-slate-400 border-slate-600 bg-slate-800/40";
  const orderStatusLabel =
    STATUS_LABELS[mailbox.orderStatus] || mailbox.orderStatus || "Unknown";
  const mailboxStatusLabel =
    MAILBOX_STATUS_LABELS[provisioning.mailboxStatus] || "Unknown";
  const mailboxStatusClass =
    MAILBOX_STATUS_COLORS[provisioning.mailboxStatus] || "text-slate-400";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl"
    >
      {/* HEADER */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-fuchsia-400">
            {mailbox.provider || "Business Email"}
          </p>
          <h3 className="mt-1 text-xl font-black text-white">
            {credentials.loginEmail || mailbox.requestedEmailAddress || "—"}
          </h3>
          <p className="mt-1 text-sm text-slate-400">{mailbox.domainName}</p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <span
            className={`rounded-full border px-3 py-1 text-xs font-bold ${orderStatusClass}`}
          >
            {orderStatusLabel}
          </span>
          <span className={`text-xs font-semibold ${mailboxStatusClass}`}>
            {mailboxStatusLabel}
          </span>
        </div>
      </div>

      {/* PLAN INFO */}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[
          ["Plan", mailbox.planName],
          ["Storage", mailbox.storage],
          ["Accounts", mailbox.emailAccounts],
          ["Domain Source", mailbox.domainSource === "asiduo_registered" ? "Managed by Asiduo" : "External Domain"],
          ["Purchased", formatDate(mailbox.createdAt)],
          ["Credentials Email", mailbox.credentialsDelivery?.status === "sent" ? `Sent to ${mailbox.credentialsDelivery.sentTo}` : mailbox.credentialsDelivery?.status || "Pending"],
        ].map(([label, val]) =>
          val ? (
            <div
              key={label}
              className="rounded-xl border border-white/5 bg-white/5 p-3"
            >
              <p className="text-xs text-slate-400">{label}</p>
              <p className="mt-1 text-sm font-semibold text-white">{val}</p>
            </div>
          ) : null
        )}
      </div>

      {/* WEBMAIL */}
      {credentials.webmailUrl ? (
        <a
          href={credentials.webmailUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:scale-105"
        >
          Open Webmail
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
            />
          </svg>
        </a>
      ) : null}

      {/* CONNECTION SETTINGS */}
      {credentials.imap?.host || credentials.smtp?.host ? (
        <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/50 p-4">
          <p className="text-sm font-bold text-sky-300">Connection Settings</p>
          <p className="mt-1 text-xs text-slate-400">
            Use these in Outlook, Thunderbird, Apple Mail, or any IMAP-compatible app.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {credentials.imap?.host ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Incoming (IMAP)
                </p>
                <Row label="Server" value={credentials.imap.host} mono />
                <Row label="Port" value={String(credentials.imap.port)} />
                <Row label="Security" value={credentials.imap.security} />
                <Row label="Username" value={credentials.loginEmail} mono />
              </div>
            ) : null}

            {credentials.smtp?.host ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Outgoing (SMTP)
                </p>
                <Row label="Server" value={credentials.smtp.host} mono />
                <Row label="Port" value={String(credentials.smtp.port)} />
                <Row label="Security" value={credentials.smtp.security} />
                <Row label="Username" value={credentials.loginEmail} mono />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* DNS RECORDS for external domains */}
      {mailbox.dnsRecordsNeeded?.length && mailbox.domainSource === "external" ? (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setShowDns((prev) => !prev)}
            className="flex items-center gap-2 text-sm font-bold text-amber-300 underline underline-offset-4"
          >
            {showDns ? "Hide" : "Show"} DNS Records To Add
          </button>

          {showDns ? (
            <div className="mt-3 space-y-2">
              {mailbox.dnsRecordsNeeded.map((record, idx) => (
                <div
                  key={`${record.type}-${record.host}-${idx}`}
                  className="rounded-xl border border-white/10 bg-slate-950/60 p-3"
                >
                  <p className="text-xs font-bold text-white">
                    {record.type}
                    {record.host ? ` — ${record.host}` : ""}
                  </p>
                  <p className="mt-1 break-all font-mono text-xs text-slate-300">
                    {record.value}
                    <CopyButton value={record.value} />
                  </p>
                  {record.purpose ? (
                    <p className="mt-1 text-xs text-slate-500">{record.purpose}</p>
                  ) : null}
                </div>
              ))}

              <p className="mt-2 text-xs text-amber-200">
                Add these records at your domain's DNS provider. Mail delivery
                activates after DNS propagation (up to 48 hours).
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* PROVISIONING NOTES */}
      {provisioning.notes ? (
        <p className="mt-4 text-xs text-slate-500">{provisioning.notes}</p>
      ) : null}
    </motion.div>
  );
};

export default function MyMailboxes() {
  const navigate = useNavigate();
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login?redirect=/my-mailboxes", { replace: true });
      return;
    }

    fetch(buildApiUrl("/api/my-mailboxes"), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Unable to load mailboxes.");
        }

        setMailboxes(data.mailboxes || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-violet-950 to-slate-900 px-6 py-28 text-white">
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-violet-500/20 blur-3xl" />
      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl" />

      <div className="mx-auto max-w-5xl">
        <div className="mb-12">
          <h1 className="text-5xl font-black leading-tight md:text-6xl">
            My
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              {" "}Mailboxes
            </span>
          </h1>
          <p className="mt-4 text-lg text-slate-300">
            All your purchased email accounts, connection settings, and DNS records in one place.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-6 text-rose-300">
            {error}
          </div>
        ) : mailboxes.length === 0 ? (
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-10 text-center backdrop-blur-xl">
            <p className="text-2xl font-black text-slate-300">No mailboxes yet</p>
            <p className="mt-3 text-slate-400">
              Purchase a business email to get your first mailbox.
            </p>
            <button
              type="button"
              onClick={() => navigate("/email")}
              className="mt-6 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-3 font-bold transition hover:scale-105"
            >
              Get Business Email
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {mailboxes.map((mailbox) => (
              <MailboxCard key={mailbox._id} mailbox={mailbox} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
